import React, { useState, useEffect, createContext, useContext, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import type { AuthContextType } from '../types';

import { useMsal } from '@azure/msal-react';

import { getSsoRequestParameters } from '../AuthConfig';

import UserData from '../classes/api/result/accountmodule/UserData';
import SessionDetailDto from '../classes/dtos/usersessionmodule/SessionDetailDto';

import UserSession from '../api/UserSession';
import UserSessionApi from '../api/UserSessionApi';

function AuthProvider(props: React.PropsWithChildren<unknown>) {
    const [user, setUser] = useState<UserData>();
    const [sessionDetail, setSessionDetail] = useState<SessionDetailDto>();
    const [loading, setLoading] = useState(true);
    const { instance } = useMsal();

    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        instance.handleRedirectPromise().then((response) => {
            if (response) {
                if (location.pathname === "/") {
                    navigate("/dashboard");
                }
            }
        }).catch(error => {
            console.error(error);
        });
    }, [instance, navigate]);

    useEffect(() => {
        (async function () {
            const result = await UserSession.EnsureUserDataAsync();
            setUser(result);
            const sessionDetailResult = await UserSessionApi.GetSessionDetailAsync();
            setSessionDetail(sessionDetailResult.Result);
            setLoading(false);
        })();
    }, []);

    const signOut = useCallback(() => {
        // An SSO account must end its session on the SSO policy, not on the regular sign-in authority.
        const account = instance.getAllAccounts()[0];
        instance.logoutRedirect({ account, authority: getSsoRequestParameters(account).authority });
    }, [instance]);

    const refreshSessionDetail = useCallback(() => {
        UserSessionApi.GetSessionDetailAsync().then((e) => {
            setSessionDetail(e.Result);
        });
    }, []);

    return (
        <AuthContext.Provider value={{ user, signOut, refreshSessionDetail, loading, sessionDetail }} {...props} />
    );
}

const AuthContext = createContext<AuthContextType>({ loading: false } as AuthContextType);
const useAuth = () => useContext(AuthContext);

export { AuthProvider, useAuth }
